import axios, { AxiosError, AxiosInstance } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  timeout: 30_000,
});

const ACCESS_TOKEN_KEY = 'grhamoy.accessToken';
const REFRESH_TOKEN_KEY = 'grhamoy.refreshToken';

export function setTokens(access: string | null, refresh: string | null) {
  if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

api.interceptors.request.use((cfg) => {
  const token = getAccessToken();
  if (token && cfg.headers) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

let refreshInFlight: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios
    .post(`${BASE_URL}/auth/refresh`, { refreshToken: refresh })
    .then((r) => {
      setTokens(r.data.accessToken, r.data.refreshToken);
      return r.data.accessToken as string;
    })
    .catch(() => {
      setTokens(null, null);
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (typeof err.config & { _retried?: boolean }) | undefined;
    if (err.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const newToken = await tryRefresh();
      if (newToken) {
        if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      // Refresh failed → bounce to login.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  },
);
